"use client";

import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AuthForm } from "@/components/auth-form";
import { ShieldCheck } from "lucide-react";

type AuthMode = "login" | "register";

interface OpenOptions {
  /** 初始模式，默认 login */
  mode?: AuthMode;
  /** 弹窗标题，默认"登录" */
  title?: string;
  /** 标题下方说明文案 */
  description?: string;
  /** 标题图标 */
  icon?: ReactNode;
  /** 登录/注册成功后的回调（补完被拦截的动作，如关注、跳转） */
  onSuccess?: () => void;
}

interface AuthDialogContextValue {
  /** 打开登录注册弹窗 */
  openAuth: (opts?: OpenOptions) => void;
  /** 关闭弹窗 */
  closeAuth: () => void;
}

const AuthDialogContext = createContext<AuthDialogContextValue>({
  openAuth: () => {},
  closeAuth: () => {},
});

/**
 * 全局登录注册弹窗 Provider。
 *
 * 设计动机：原先每个 useLoginGate / header / subscription-drawer 各自挂载 Dialog 实例，
 * 首页列表有几十个 AffinityChip → 几十个 Dialog 实例常驻 DOM，状态混乱且焦点互相干扰。
 * 改为全局单例：整个应用只挂载一个 Dialog，所有调用点通过 context 触发。
 *
 * Dialog 默认 modal=true（焦点锁定 + 遮罩阻挡点击穿透），避免点击注册表单时
 * 事件穿透到下层词条行而反复呼出 TopicDrawer。
 */
export function AuthDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<AuthMode>("login");
  const [title, setTitle] = useState("登录");
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [icon, setIcon] = useState<ReactNode | undefined>(undefined);
  const onSuccessRef = useRef<(() => void) | null>(null);

  const openAuth = useCallback((opts: OpenOptions = {}) => {
    const nextMode = opts.mode ?? "login";
    setMode(nextMode);
    setTitle(opts.title ?? (nextMode === "login" ? "登录" : "注册"));
    setDescription(opts.description);
    setIcon(opts.icon);
    onSuccessRef.current = opts.onSuccess ?? null;
    setOpen(true);
  }, []);

  const closeAuth = useCallback(() => {
    setOpen(false);
    onSuccessRef.current = null;
  }, []);

  const handleSuccess = useCallback(() => {
    setOpen(false);
    const fn = onSuccessRef.current;
    onSuccessRef.current = null;
    // AuthProvider.login 已写入 token 并更新 user，直接补完被拦截动作
    if (fn) fn();
  }, []);

  return (
    <AuthDialogContext.Provider value={{ openAuth, closeAuth }}>
      {children}
      {/* 全局唯一的登录注册弹窗 — modal 默认 true，焦点锁定 + 遮罩防穿透 */}
      {open && (
        <Dialog open={open} onOpenChange={setOpen} modal="trap-focus">
          <DialogContent className="max-w-[calc(100%-1.5rem)] sm:max-w-md p-0 overflow-hidden">
            <div className="border-b bg-muted/30 px-5 py-4">
              <DialogHeader>
                <DialogTitle className="text-left flex items-center gap-2 text-lg">
                  {icon ?? <ShieldCheck className="w-5 h-5" />}
                  {title}
                </DialogTitle>
                {description && (
                  <p className="text-sm leading-6 text-muted-foreground whitespace-pre-line">
                    {description}
                  </p>
                )}
              </DialogHeader>
            </div>
            <div className="px-5 py-5">
              <AuthForm
                mode={mode}
                allowSwitch
                onSuccess={handleSuccess}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </AuthDialogContext.Provider>
  );
}

/** 便捷 hook：打开登录注册弹窗 */
export function useAuthDialog() {
  return useContext(AuthDialogContext);
}
